/**
 * What the two voice screens agree on: how a voice is named on screen, how much
 * of its description a card has room for, and which voices a message creator
 * may offer.
 *
 * The booth stores the **disabled** ids, so a list the API sends without a
 * `disabled` field means nothing has been curated out.
 */

import type { VoiceList, VoiceOption } from "./api.ts";

/** Engine voice names arrive lower-cased; a card reads better with a capital. */
export function voiceLabel(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function isVoiceEnabled(list: VoiceList, id: string): boolean {
  return !(list.disabled ?? []).includes(id);
}

/** The engine's default, as an id, so every screen selects the same voice. */
export function defaultVoiceId(list: VoiceList): string {
  const named = list.voices.find((option) => option.name === list.defaultVoice);
  return (named ?? list.voices[0])?.id ?? "";
}

/**
 * The voices a message creator draws. The enabled ones, plus whichever one is
 * selected — the default voice can be disabled, and preselecting a card that is
 * not on the screen would leave the person with nothing to change.
 */
export function offeredVoices(list: VoiceList, selected: string): VoiceOption[] {
  return list.voices.filter(
    (option) => option.id === selected || isVoiceEnabled(list, option.id),
  );
}

/** The name for a stored voice id, or null when the account no longer has it. */
export function voiceNameById(voices: VoiceOption[], id: string): string | null {
  const found = voices.find((option) => option.id === id);
  return found ? voiceLabel(found.name) : null;
}

/**
 * The letter a voice's circle carries. Emoji were tried and dropped: hashing
 * twenty ids onto a fixed list collided, and two voices wearing the same bird
 * implied a kinship between them that does not exist. An initial can repeat
 * without saying anything untrue, because the name it comes from is beside it.
 */
export function voiceInitial(name: string): string {
  return (name.charAt(0) || "?").toUpperCase();
}

/**
 * The half of a description that tells voices apart. The engine builds it as
 * "male, middle aged, british, mature — Warm, Captivating Storyteller": the
 * traits before the dash are near-identical across the account, and the
 * character after it is the part worth the space on a card. The catalog shows
 * the whole string; a card shows this.
 */
export function voiceCharacter(description: string): string {
  const [traits, character] = description.split(" — ");
  return (character ?? traits ?? "").trim() || description;
}
